import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {hasPermission, useWardrobe} from "../context/WardrobeContext";
import AuthImage from "../components/AuthImage";
import Spinner from "../components/Spinner";
import {ChevronDownIcon, DisposalIcon, PlusIcon, TrashIcon, XIcon} from "../components/icons";
import {pluralItems} from '../utils/plural';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface ClothingItemPreview {
    id: string;
    name: string;
    firstPhotoUrl: string | null;
    thrownAway?: boolean;
}

interface ClothingListDto {
    id: string;
    name: string;
    description: string | null;
    itemCount: number;
    previewItems?: ClothingItemPreview[];
}

/** Тело ошибки backend ({@code GlobalExceptionHandler}): {@code {error, code, description}}. */
interface ApiErrorLike {
    response?: {status?: number; data?: {error?: string; description?: string}};
}

const CREATE_ERROR_FALLBACK = 'Не удалось создать список. Попробуйте ещё раз.';

/**
 * Текст ошибки создания для инлайн-показа у поля (styleguide §Forms: ошибка рядом с полем,
 * не только цветом). Берём понятное описание backend ({@code description}, затем {@code error}),
 * иначе — общий fallback; сетевые ошибки без ответа — тоже fallback.
 */
export const createListErrorText = (err: unknown): string => {
    const data = (err as ApiErrorLike | undefined)?.response?.data;
    const text = data?.description?.trim() || data?.error?.trim();
    return text || CREATE_ERROR_FALLBACK;
};

const ClothingListsPage: React.FC = () => {
    const navigate = useNavigate();
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe, permissions} = useWardrobe();
    const canCreateList = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LISTS');
    const canDeleteList = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LISTS');
    const [lists, setLists] = useState<ClothingListDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const fetchLists = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ClothingListDto[]>('/lists', {params: selectedOwnerId ? {ownerId: selectedOwnerId} : {}});
            setLists(res.data ?? []);
        } catch (err) {
            console.error('Error fetching lists:', err);
            setLists([]);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useDocumentTitle();

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    const openCreateModal = () => {
        setCreateError(null);
        setCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        if (creating) return;
        setCreateModalOpen(false);
        setCreateError(null);
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        setCreateError(null);
        try {
            const api = await createAxiosInstance();
            await api.post('/lists', {name: newName.trim(), description: newDescription.trim() || null}, {params: selectedOwnerId ? {ownerId: selectedOwnerId} : {}});
            setCreateModalOpen(false);
            setNewName('');
            setNewDescription('');
            fetchLists();
        } catch (err) {
            // Форма остаётся открытой, ошибка — рядом с полем (не только глобальный баннер).
            console.error('Error creating list:', err);
            setCreateError(createListErrorText(err));
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!window.confirm('Удалить список?')) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/lists/${id}`, {params: selectedOwnerId ? {ownerId: selectedOwnerId} : {}});
            fetchLists();
        } catch (err) {
            console.error('Error deleting list:', err);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {isOwnWardrobe ? 'Списки вещей' : `Списки: ${selectedOwnerDisplayName || selectedOwnerId?.slice(0, 8) + '...'}`}
                </h1>
                {canCreateList && (
                <button
                    onClick={openCreateModal}
                    data-testid="lists-create-open"
                    className="btn-primary flex items-center gap-2"
                >
                    <PlusIcon className="w-5 h-5"/>
                    Создать список
                </button>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Spinner/>
                </div>
            ) : lists.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    Нет списков. Создайте первый список.
                </div>
            ) : (
                <div className="grid gap-3">
                    {lists.map((list) => (
                        <div
                            key={list.id}
                            onClick={() => navigate(`/lists/${list.id}`)}
                            className="flex flex-col gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer transition-colors"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{list.name}</h3>
                                    {list.description && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{list.description}</p>
                                    )}
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                        {pluralItems(list.itemCount)}
                                    </p>
                                </div>
                                {canDeleteList && (
                                <button
                                    onClick={(e) => handleDelete(e, list.id)}
                                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                                    title="Удалить"
                                >
                                    <TrashIcon className="w-5 h-5"/>
                                </button>
                                )}
                            </div>
                            {(list.previewItems?.length ?? 0) > 0 && (
                                <div className="flex -space-x-3">
                                    {list.previewItems!.slice(0, 4).map((item) => (
                                        <div
                                            key={item.id}
                                            className="w-12 h-12 rounded-lg border-2 border-white dark:border-gray-800 overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 relative"
                                            title={item.name}
                                        >
                                            {item.firstPhotoUrl ? (
                                                <AuthImage
                                                    imageKey={item.firstPhotoUrl}
                                                    alt={item.name}
                                                    variant="thumbnail"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</div>
                                            )}
                                            {item.thrownAway && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                                    <DisposalIcon className="w-5 h-5 text-white"/>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {createModalOpen && (
                <div
                    className="fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={closeCreateModal}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="list-create-title"
                        data-testid="list-create-dialog"
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="list-create-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Новый список</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="list-create-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Название</label>
                                <input
                                    id="list-create-name"
                                    type="text"
                                    value={newName}
                                    onChange={(e) => {
                                        setNewName(e.target.value);
                                        if (createError) setCreateError(null);
                                    }}
                                    aria-invalid={!!createError}
                                    aria-describedby={createError ? 'list-create-error' : undefined}
                                    className={`input-field w-full ${createError
                                        ? 'border-red-500 dark:border-red-500 focus:ring-red-500/40 focus:border-red-500'
                                        : ''}`}
                                    placeholder="Например: Лето 2025"
                                />
                                {createError && (
                                    <p
                                        id="list-create-error"
                                        role="alert"
                                        data-testid="list-create-error"
                                        className="mt-1.5 text-sm text-red-600 dark:text-red-400"
                                    >
                                        {createError}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="list-create-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Описание (необязательно)</label>
                                <textarea
                                    id="list-create-description"
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    className="input-field w-full"
                                    rows={3}
                                    placeholder="Описание списка"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                type="button"
                                onClick={closeCreateModal}
                                className="btn-secondary"
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={!newName.trim() || creating}
                                data-testid="list-create-submit"
                                className="btn-primary"
                            >
                                {creating ? 'Создание...' : 'Создать'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClothingListsPage;
